import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, Search, Phone, MessageCircle } from "lucide-react";
import { fmtINR, mediaUrl } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CatalogView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/catalog/${token}`);
        setData(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Catalog not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[color:var(--mecha-red)]" />
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)]">
            // 404
          </div>
          <div className="font-display text-4xl mt-3">CATALOG UNAVAILABLE</div>
          <div className="text-zinc-400 text-sm mt-4">{error}</div>
        </div>
      </div>
    );

  const visible = q.trim()
    ? data.products.filter((p) => {
        const t = q.toLowerCase();
        return (
          p.name.toLowerCase().includes(t) ||
          p.sku.toLowerCase().includes(t) ||
          p.category?.toLowerCase().includes(t)
        );
      })
    : data.products;

  const f = data.filters || {};
  const filterChips = [];
  if (f.category) filterChips.push(f.category.toUpperCase());
  if (f.min_price != null) filterChips.push(`FROM ${fmtINR(f.min_price)}`);
  if (f.max_price != null) filterChips.push(`UP TO ${fmtINR(f.max_price)}`);
  if (f.in_stock_only) filterChips.push("IN STOCK");

  const waEnquire = (p) => {
    const phone = (data.store_phone || "").replace(/[^\d]/g, "");
    const msg = encodeURIComponent(
      `Hi Swagg Squad! I'd like to grab this from the "${data.name}" catalog:\n\n${p.name}\nSKU: ${p.sku}\nPrice: ${fmtINR(p.price)}`
    );
    return `https://wa.me/${phone.startsWith("91") ? phone : "91" + phone}?text=${msg}`;
  };

  return (
    <div className="min-h-screen">
      <Toaster theme="dark" position="top-right" />

      {/* Sticky header */}
      <header className="sticky top-0 z-30 border-b border-zinc-800 bg-black/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {data.store_logo_url && (
              <div className="w-11 h-11 bg-white p-1 shrink-0">
                <img
                  src={data.store_logo_url}
                  alt={data.store_name}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-display text-lg leading-none truncate">
                SWAGG<span className="text-[color:var(--mecha-red)]">.</span>SQUAD
              </div>
              <div className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 mt-1">
                {data.store_name}
              </div>
            </div>
          </div>
          {data.store_phone && (
            <a
              href={`https://wa.me/${(data.store_phone || "").replace(/[^\d]/g, "").replace(/^91?/, "91")}?text=${encodeURIComponent(`Hi Swagg Squad! I saw your "${data.name}" catalog.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-brutal h-10 px-4 flex items-center gap-2 text-white text-xs font-mono tracking-widest uppercase"
              style={{ background: "#25D366" }}
            >
              <MessageCircle className="h-4 w-4" />
              <span className="hidden sm:inline">CHAT</span>
            </a>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10 lg:py-14">
        <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)]">
          // CURATED FOR YOU
        </div>
        <h1 className="font-display text-4xl lg:text-6xl mt-3 leading-[0.95]">
          {data.name.toUpperCase()}
        </h1>
        <div className="flex flex-wrap items-center gap-2 mt-5">
          {filterChips.map((c) => (
            <span
              key={c}
              className="font-mono text-[10px] tracking-widest bg-white text-black px-2 py-1 font-bold"
            >
              {c}
            </span>
          ))}
          <span
            data-testid="catalog-count"
            className="font-mono text-[10px] tracking-widest border border-zinc-800 text-zinc-400 px-2 py-1"
          >
            {data.count} PIECES
          </span>
        </div>

        <div className="relative mt-8 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <input
            data-testid="catalog-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search within this catalog..."
            className="w-full h-12 bg-zinc-950 border border-zinc-800 pl-12 pr-4 text-white placeholder-zinc-600 focus:outline-none focus:border-[color:var(--mecha-red)]"
          />
        </div>
      </section>

      {/* Products */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pb-20">
        {visible.length === 0 ? (
          <div className="border border-zinc-800 p-16 text-center">
            <div className="text-zinc-400">
              No products match your search inside this catalog.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
            {visible.map((p) => (
              <div
                key={p.id}
                data-testid={`cat-prod-${p.sku}`}
                className="card-brutal hoverable-card overflow-hidden flex flex-col"
              >
                <div className="aspect-square bg-zinc-900">
                  {p.image_url ? (
                    <img
                      src={mediaUrl(p.image_url)}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-700 font-display text-6xl">
                      {p.name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="p-3 lg:p-4 flex-1 flex flex-col">
                  <div className="font-mono text-[9px] tracking-widest text-zinc-500">
                    {p.category?.toUpperCase()}
                  </div>
                  <div className="font-medium text-sm mt-1 line-clamp-2 min-h-[2.5rem]">
                    {p.name}
                  </div>
                  <div className="flex items-end justify-between mt-3">
                    <div className="font-mono font-bold text-[color:var(--mecha-red)]">
                      {fmtINR(p.price)}
                    </div>
                    <div
                      className={`font-mono text-[9px] tracking-widest ${
                        p.stock <= 3 ? "text-[color:var(--warn)]" : "text-zinc-500"
                      }`}
                    >
                      {p.stock} LEFT
                    </div>
                  </div>
                  {data.store_phone && (
                    <a
                      href={waEnquire(p)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`enquire-${p.sku}`}
                      className="btn-brutal mt-3 h-9 w-full flex items-center justify-center text-white font-mono text-[10px] tracking-widest uppercase"
                      style={{ background: "#25D366" }}
                    >
                      Grab on WhatsApp
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 px-6 py-8">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-600">
            // CURATED LINK · {data.store_name}
          </div>
          {data.store_phone && (
            <a
              href={`tel:${data.store_phone}`}
              className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white"
            >
              <Phone className="h-3 w-3" />
              {data.store_phone}
            </a>
          )}
        </div>
      </footer>
    </div>
  );
}
